import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import * as PackInFocusActions from './pack-in-focus.actions';
import * as PackInFocusSelectors from './pack-in-focus.selectors';
import { combineLatest, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { PackInFocusData } from './pack-in-focus.models';
import { QuoteModel } from '@cpak/adp-cpak-quotes-service-models';
import {
  deepCopy,
  generateKeyForChangedProductsSet,
  getIsBomDisabled,
  mergeProductDataToPackData,
} from '@cpak/shared/utils';
import { UserFacade } from '../../global-config-store/state/user/user.facade';
import { PacksNotSavedChangesFacade } from '../../packs-not-saved-changes/state/packs-not-saved-changes.facade';
import { PackNotSavedChangesEntity } from '../../packs-not-saved-changes/state/packs-not-saved-changes.reducer';
import { ProductPageDataWithQuantity } from '@cpak/interfaces';
import { BOMPartModel } from '@cpak/adp-cpak-quotes-service-models';

@Injectable()
export class PackInFocusFacade {
  constructor(
    private readonly store: Store,
    private userFacade: UserFacade,
    private packsNotSavedChangesFacade: PacksNotSavedChangesFacade
  ) {}

  public getData(): Observable<PackInFocusData | null> {
    return this.store.pipe(select(PackInFocusSelectors.getPackInFocusData()));
  }

  public setData(packInFocus: PackInFocusData): void {
    this.store.dispatch(PackInFocusActions.setPackInFocus({ data: packInFocus }));
  }

  private getPackInFocusAndNotSavedChanges(): Observable<{
    packInFocusData: PackInFocusData | null;
    notSavedChanges: PackNotSavedChangesEntity | null;
  }> {
    return this.getData().pipe(
      take(1),
      switchMap(packInFocusData => {
        if (packInFocusData?.id) {
          return this.packsNotSavedChangesFacade.getPackChanges(packInFocusData.id).pipe(
            take(1),
            map(notSavedChanges => {
              return {
                packInFocusData,
                notSavedChanges: notSavedChanges || null,
              };
            })
          );
        } else {
          return of({ packInFocusData, notSavedChanges: null });
        }
      })
    );
  }

  public addPart(addedItem: ProductPageDataWithQuantity): void {
    this.updatePart(addedItem);
  }
  public replacePart(newItem: ProductPageDataWithQuantity, oldItemProductId: string, oldItemLocationId: string): void {
    this.updatePart(newItem, oldItemProductId, oldItemLocationId);
  }

  private updatePart(
    newItem: ProductPageDataWithQuantity,
    oldItemProductId: string | null = null,
    oldItemLocationId: string | null = null
  ): void {
    this.getPackInFocusAndNotSavedChanges()
      .pipe(
        take(1),
        tap(({ packInFocusData, notSavedChanges }) => {
          if (packInFocusData) {
            let { bom, packData, changedProducts } =
              (notSavedChanges && deepCopy(notSavedChanges)) || deepCopy(packInFocusData) || {};

            packData = mergeProductDataToPackData(packData, newItem.productData);

            const alreadyAddedPart = bom.parts.find(
              part => part.productId === newItem.productData.product.id && part.locationId === newItem.locationId
            );
            const partsWithoutOldPart = oldItemProductId
              ? bom.parts.filter(
                  part =>
                    !oldItemProductId || part.productId !== oldItemProductId || part.locationId !== oldItemLocationId
                )
              : bom.parts;
            if (alreadyAddedPart) {
              alreadyAddedPart.quantity += newItem.quantity;
              bom.parts = partsWithoutOldPart;
            } else {
              const newBomPart: BOMPartModel = {
                id: '',
                productId: newItem.productData.product.id,
                quantity: newItem.quantity,
                locationId: newItem.locationId,
              };
              bom.parts = [...partsWithoutOldPart, newBomPart];
            }

            const key = generateKeyForChangedProductsSet(newItem.productData.product.id, newItem.locationId);
            if (!changedProducts.includes(key)) {
              changedProducts.push(key);
            }
            this.packsNotSavedChangesFacade.addBOMChangesToStore(packInFocusData.id, packData, changedProducts, bom);

            this.setData({
              ...packInFocusData,
              packData,
              changedProducts,
              bom,
            });
          }
        })
      )
      .subscribe();
  }

  public clear(): void {
    this.store.dispatch(PackInFocusActions.clearPackInFocus());
  }

  public checkQuoteAndClear(quote: QuoteModel): void {
    combineLatest([this.getData().pipe(take(1)), this.userFacade.getUserId().pipe(take(1))]).subscribe(
      ([data, userId]) => {
        if (data && userId && data.id === quote.id && getIsBomDisabled(quote, userId)) {
          this.store.dispatch(PackInFocusActions.clearPackInFocus());
        }
      }
    );
  }
}
